<script lang="ts">
	import { _ } from 'svelte-i18n';
	import Header from '$lib/components/sections/Header.svelte';
	import Footer from '$lib/components/sections/Footer.svelte';
	
	const lastUpdated = 'December 2024';
	const effectiveDate = 'December 1, 2024';
</script>

<svelte:head>
	<title>{$_('pages.privacyPolicy.meta.title')} - Heyweek</title>
	<meta name="description" content={$_('pages.privacyPolicy.meta.description')} />
	<meta property="og:title" content="{$_('pages.privacyPolicy.meta.title')} - Heyweek" />
	<meta property="og:description" content={$_('pages.privacyPolicy.meta.description')} />
</svelte:head>

<Header />

<main class="legal-page">
	<div class="container">
		<div class="legal-header">
			<h1 class="legal-title">{$_('pages.privacyPolicy.title')}</h1>
			<p class="legal-meta">{$_('pages.privacyPolicy.lastUpdated')}: {lastUpdated}</p>
		</div>
		
		<div class="legal-content">
			<section class="legal-section">
				<p class="intro">
					Your data privacy and security are important to us. This Privacy Policy explains how Heyweek ("we", "us", "our", or the "Company") collects, uses, shares, and protects information in relation to our website heyweek.com and our services (collectively, the "Services").
				</p>
				<p>
					By using our Services, you agree to be bound by this Privacy Policy. If you don't agree to this Privacy Policy, do not use the Services.
				</p>
			</section>

			<section class="legal-section">
				<h2>1. Information We Collect</h2>
				
				<h3>Personal Information</h3>
				<p>
					Personal information is data that can be used to identify or contact you. We collect personal information you voluntarily provide to us when you:
				</p>
				<ul>
					<li>Register for an account (name, email address, password)</li>
					<li>Use our Services (time entries, project data, client information)</li>
					<li>Contact us for support or inquiries</li>
					<li>Subscribe to our newsletter or marketing communications</li>
					<li>Participate in surveys or feedback collection</li>
				</ul>

				<h3>Non-Personal Information</h3>
				<p>
					We automatically collect certain non-personal information when you visit or use our Services:
				</p>
				<ul>
					<li>IP address and location data</li>
					<li>Browser type and version</li>
					<li>Operating system</li>
					<li>Device information</li>
					<li>Usage data and analytics</li>
					<li>Cookies and similar tracking technologies</li>
				</ul>
			</section>

			<section class="legal-section">
				<h2>2. How We Use Your Information</h2>
				<p>
					We use the information we collect for the following purposes:
				</p>
				<ul>
					<li><strong>Service Provision:</strong> To provide, maintain, and improve our Services</li>
					<li><strong>Account Management:</strong> To manage your account and provide customer support</li>
					<li><strong>Communication:</strong> To send you updates, newsletters, and marketing materials (with your consent)</li>
					<li><strong>Analytics:</strong> To understand how users interact with our Services</li>
					<li><strong>Security:</strong> To detect, prevent, and address technical issues and protect against fraud</li>
					<li><strong>Legal Compliance:</strong> To comply with legal obligations and enforce our terms</li>
					<li><strong>Product Development:</strong> To develop new features and improve existing ones</li>
				</ul>
			</section>

			<section class="legal-section">
				<h2>3. How We Share Your Information</h2>
				<p>
					We do not sell your personal information. We only share your information in the following circumstances:
				</p>
				<ul>
					<li><strong>With Your Consent:</strong> We share information when you give us explicit permission</li>
					<li><strong>Service Providers:</strong> With trusted third-party vendors who help us operate our Services (e.g., hosting, analytics, payment processing)</li>
					<li><strong>Legal Requirements:</strong> When required by law or to respond to legal process</li>
					<li><strong>Protection of Rights:</strong> To protect our rights, privacy, safety, or property, and that of our users</li>
					<li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
				</ul>
			</section>

			<section class="legal-section" id="security">
				<h2>4. Data Storage and Security</h2>
				<p>
					<strong>Location:</strong> Your data is stored on secure servers located in the European Union.
				</p>
				<p>
					<strong>Security Measures:</strong> We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
				</p>
				<ul>
					<li>Encryption of data in transit and at rest</li>
					<li>Regular security audits and updates</li>
					<li>Access controls and authentication</li>
					<li>Employee training on data protection</li>
					<li>Secure backup procedures</li>
				</ul>
				<p class="note">
					However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to protect your personal information, we cannot guarantee its absolute security.
				</p>
			</section>

			<section class="legal-section">
				<h2>5. Data Retention</h2>
				<p>
					We retain your personal information for as long as necessary to:
				</p>
				<ul>
					<li>Provide you with our Services</li>
					<li>Comply with legal obligations</li>
					<li>Resolve disputes and enforce our agreements</li>
					<li>Fulfill the purposes outlined in this Privacy Policy</li>
				</ul>
				<p>
					When you delete your account, we will delete or anonymize your personal information within 30 days, except where we are required to retain it for legal or legitimate business purposes.
				</p>
			</section>

			<section class="legal-section" id="gdpr">
				<h2>6. Your Rights and Choices</h2>
				<p>
					You have certain rights regarding your personal information:
				</p>
				<ul>
					<li><strong>Access:</strong> Request a copy of your personal information</li>
					<li><strong>Correction:</strong> Update or correct inaccurate information</li>
					<li><strong>Deletion:</strong> Request deletion of your account and personal information</li>
					<li><strong>Portability:</strong> Receive your data in a portable format</li>
					<li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
					<li><strong>Restriction:</strong> Request that we limit processing of your data</li>
				</ul>
				<p>
					To exercise any of these rights, please contact us at <a href="mailto:hello@heyweek.com">hello@heyweek.com</a>.
				</p>
			</section>

			<section class="legal-section" id="cookies">
				<h2>7. Cookies and Tracking Technologies</h2>
				<p>
					We use cookies and similar tracking technologies to:
				</p>
				<ul>
					<li>Remember your preferences and settings</li>
					<li>Understand how you use our Services</li>
					<li>Provide targeted advertising</li>
					<li>Improve our Services</li>
				</ul>
				<p>
					You can control cookies through your browser settings. However, disabling cookies may limit your ability to use certain features of our Services.
				</p>
			</section>

			<section class="legal-section">
				<h2>8. Children's Privacy</h2>
				<p>
					Our Services are not intended for children under 17 years of age. We do not knowingly collect personal information from children under 17. If we become aware that we have collected personal information from a child under 17, we will take steps to delete such information.
				</p>
			</section>

			<section class="legal-section">
				<h2>9. International Data Transfers</h2>
				<p>
					If you access our Services from outside the European Union, your information may be transferred to, stored, and processed in the EU. By using our Services, you consent to this transfer.
				</p>
			</section>

			<section class="legal-section">
				<h2>10. Third-Party Links</h2>
				<p>
					Our Services may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies.
				</p>
			</section>

			<section class="legal-section">
				<h2>11. Changes to This Privacy Policy</h2>
				<p>
					We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. Your continued use of our Services after any changes indicates your acceptance of the updated Privacy Policy.
				</p>
			</section>

			<section class="legal-section">
				<h2>12. Contact Us</h2>
				<p>
					If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
				</p>
				<div class="contact-info">
					<p><strong>Heyweek</strong></p>
					<p>Email: <a href="mailto:hello@heyweek.com">hello@heyweek.com</a></p>
					<p>Address: Stockholm, Sweden</p>
				</div>
			</section>
		</div>
	</div>
</main>

<Footer />

<style>
	.legal-page {
		padding-block: var(--space-16);
		background: var(--bg-primary);
		min-height: 100vh;
	}
	
	.container {
		max-width: 800px;
		margin-inline: auto;
		padding-inline: var(--container-padding);
	}
	
	.legal-header {
		text-align: center;
		padding-bottom: var(--space-12);
		border-bottom: 1px solid var(--border-light);
		margin-bottom: var(--space-12);
	}
	
	.legal-title {
		font-size: var(--font-size-4xl);
		font-weight: var(--font-weight-bold);
		color: var(--text-primary);
		margin-bottom: var(--space-4);
	}
	
	.legal-meta {
		font-size: var(--font-size-base);
		color: var(--text-secondary);
	}
	
	.legal-content {
		color: var(--text-secondary);
		line-height: var(--line-height-relaxed);
	}
	
	.legal-section {
		margin-bottom: var(--space-12);
	}
	
	.legal-section h2 {
		font-size: var(--font-size-2xl);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
		padding-top: var(--space-4);
	}
	
	.legal-section h3 {
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		color: var(--text-primary);
		margin-top: var(--space-6);
		margin-bottom: var(--space-3);
	}
	
	.legal-section p {
		margin-bottom: var(--space-4);
	}
	
	.legal-section ul {
		margin-left: var(--space-6);
		margin-bottom: var(--space-4);
		list-style: disc;
	}
	
	.legal-section li {
		margin-bottom: var(--space-2);
		line-height: var(--line-height-relaxed);
	}
	
	.legal-section a {
		color: var(--text-brand);
		text-decoration: underline;
		text-underline-offset: var(--text-underline-offset);
		transition: var(--transition-colors);
	}
	
	.legal-section a:hover {
		color: var(--brand-dark);
	}
	
	.intro {
		font-size: var(--font-size-lg);
		color: var(--text-primary);
		margin-bottom: var(--space-6);
	}
	
	.note {
		padding: var(--space-4);
		background: var(--bg-secondary);
		border-left: 3px solid var(--brand-primary);
		border-radius: var(--radius-md);
		margin-top: var(--space-4);
		font-size: var(--font-size-sm);
	}
	
	.contact-info {
		padding: var(--space-6);
		background: var(--bg-secondary);
		border-radius: var(--radius-lg);
		margin-top: var(--space-6);
	}
	
	.contact-info p {
		margin-bottom: var(--space-2);
	}
	
	/* Responsive */
	@media (max-width: 768px) {
		.legal-title {
			font-size: var(--font-size-3xl);
		}
		
		.legal-section h2 {
			font-size: var(--font-size-xl);
		}
		
		.legal-section ul {
			margin-left: var(--space-4);
		}
	}
</style>